// Keeps scms_embedding in step with the CMS: embeds new or changed sections, removes ones that are gone
import { cmsDb } from "../persistence/client"
import { createEmbeddingsBatch } from "@/lib/agents/search/documents"
import { sectionKey, type IndexSection, type SearchArea } from "./sections"

const TABLE = "scms_embedding"
const PAGE = 1000
const EMBED_BATCH = 64
const DELETE_BATCH = 200

export interface IndexedRow {
  id: number
  area: SearchArea
  kind: IndexSection["kind"]
  record_id: string
  locale: string
  chunk: number
  content_hash: string
}

export interface SyncPlan {
  embed: IndexSection[]
  remove: number[]
  unchanged: number
}

export interface SyncProgress {
  done: number
  total: number
}

const rowKey = (r: IndexedRow) =>
  sectionKey({ area: r.area, kind: r.kind, recordId: r.record_id, locale: r.locale, chunk: r.chunk })

export async function loadIndex(team: string, areas: SearchArea[]): Promise<IndexedRow[]> {
  if (!areas.length) return []
  const rows: IndexedRow[] = []
  for (let from = 0; ; from += PAGE) {
    const { data, error } = await cmsDb.from(TABLE)
      .select("id, area, kind, record_id, locale, chunk, content_hash")
      .eq("team_id", team).in("area", areas)
      .order("id").range(from, from + PAGE - 1)
    if (error) throw new Error(error.message ?? String(error))
    rows.push(...((data ?? []) as IndexedRow[]))
    if (!data || data.length < PAGE) return rows
  }
}

// Whether the team has any stored vectors yet, i.e. whether meaning search is on
export async function hasIndex(team: string): Promise<boolean> {
  const { data, error } = await cmsDb.from(TABLE).select("id").eq("team_id", team).limit(1)
  if (error) throw new Error(error.message ?? String(error))
  return !!data?.length
}

export function planSync(sections: IndexSection[], existing: IndexedRow[]): SyncPlan {
  const stored = new Map(existing.map(r => [rowKey(r), r]))
  const embed: IndexSection[] = []
  let unchanged = 0
  const keep = new Set<string>()
  for (const s of sections) {
    const key = sectionKey(s)
    keep.add(key)
    if (stored.get(key)?.content_hash === s.hash) unchanged++
    else embed.push(s)
  }
  const remove = existing.filter(r => !keep.has(rowKey(r))).map(r => r.id)
  return { embed, remove, unchanged }
}

export async function applySync(
  team: string,
  plan: SyncPlan,
  apiKey: string,
  onProgress?: (p: SyncProgress) => void,
  signal?: AbortSignal,
): Promise<void> {
  const total = plan.embed.length + plan.remove.length
  let done = 0
  onProgress?.({ done, total })

  for (let i = 0; i < plan.embed.length; i += EMBED_BATCH) {
    if (signal?.aborted) return
    const batch = plan.embed.slice(i, i + EMBED_BATCH)
    const vectors = await createEmbeddingsBatch(batch.map(s => s.content), apiKey)
    const now = new Date().toISOString()
    const { error } = await cmsDb.from(TABLE).upsert(
      batch.map((s, j) => ({
        team_id: team, area: s.area, kind: s.kind, record_id: s.recordId, locale: s.locale, chunk: s.chunk,
        content: s.content, content_hash: s.hash, embedding: vectors[j], updated_at: now,
      })),
      { onConflict: "team_id,area,kind,record_id,locale,chunk" },
    )
    if (error) throw new Error(error.message ?? String(error))
    done += batch.length
    onProgress?.({ done, total })
  }

  for (let i = 0; i < plan.remove.length; i += DELETE_BATCH) {
    if (signal?.aborted) return
    const ids = plan.remove.slice(i, i + DELETE_BATCH)
    const { error } = await cmsDb.from(TABLE).delete().in("id", ids)
    if (error) throw new Error(error.message ?? String(error))
    done += ids.length
    onProgress?.({ done, total })
  }
}

// Turning meaning search off: every stored vector for these areas goes
export async function clearIndex(team: string, areas: SearchArea[]): Promise<void> {
  if (!areas.length) return
  const { error } = await cmsDb.from(TABLE).delete().eq("team_id", team).in("area", areas)
  if (error) throw new Error(error.message ?? String(error))
}

export interface StoredSection {
  locale: string
  chunk: number
  content: string
  content_hash: string
  updated_at: string
}

// What's stored for one record, for the editor's index view
export async function loadRecordIndex(team: string, area: SearchArea, kind: IndexSection["kind"], recordId: string): Promise<StoredSection[]> {
  const { data, error } = await cmsDb.from(TABLE)
    .select("locale, chunk, content, content_hash, updated_at")
    .eq("team_id", team).eq("area", area).eq("kind", kind).eq("record_id", recordId)
    .order("chunk")
  if (error) throw new Error(error.message ?? String(error))
  return (data ?? []) as StoredSection[]
}
